import { c as _c } from "react/compiler-runtime";
import { useQuery } from "react-query";
import { useApi } from "./useApi";
import { useActiveGithubOrgForUserNonSuspense } from "./useConnectedGithubOrgs";
import { safeLocalStorage } from "@/lib/storage";
import { type NamedOrg, orgApiPath, orgStorageKey } from "@/lib/named-org";
import * as z from "zod";

// Schema for cached workflow/job data
const CachedWorkflowData = z.object({
  workflows: z.array(z.string()),
  jobs: z.array(z.object({
    workflow_name: z.string(),
    job_name: z.string()
  })),
  timestamp: z.number()
});
type CachedWorkflowData = z.infer<typeof CachedWorkflowData>;
const CACHE_KEY_PREFIX = "blacksmith_workflow_cache_";
const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours
// Guardrails: this cache is only used to power the command palette search.
// For very large orgs, storing *everything* can exceed browser storage quota and
// cause downstream issues.
const MAX_CACHED_WORKFLOWS = 200;
const MAX_CACHED_JOBS = 1000;
const MAX_CACHE_BYTES_PER_ORG = 300 * 1024; // 300KB

function getUtf8BytesLength(input: string): number {
  // TextEncoder is widely supported in modern browsers; keep a safe fallback.
  try {
    return new TextEncoder().encode(input).length;
  } catch {
    // Worst-case fallback: assume 2 bytes per char (overestimates for ASCII)
    return input.length * 2;
  }
}

// Org names repeat across GitHub instances and localStorage outlives a login.
function getWorkflowCacheKey(org: NamedOrg): string {
  return orgStorageKey(CACHE_KEY_PREFIX, org);
}
function listWorkflowCacheKeys(): string[] {
  return safeLocalStorage.getKeysWithPrefix(CACHE_KEY_PREFIX);
}
function getCacheTimestampFromKey(key: string): number {
  const raw = safeLocalStorage.get(key);
  if (!raw) return 0;
  try {
    const parsed = JSON.parse(raw);
    const validated = CachedWorkflowData.safeParse(parsed);
    return validated.success ? validated.data.timestamp : 0;
  } catch {
    return 0;
  }
}
function evictOldWorkflowCacheEntries(exceptKey: string): string[] {
  const keys = listWorkflowCacheKeys().filter(k => k !== exceptKey);
  const keysWithTs = keys.map(k => ({
    key: k,
    ts: getCacheTimestampFromKey(k)
  }));
  keysWithTs.sort((a, b) => a.ts - b.ts); // oldest first
  const removed: string[] = [];
  for (const {
    key
  } of keysWithTs) {
    safeLocalStorage.remove(key);
    removed.push(key);
  }
  return removed;
}
function normalizeWorkflowCachePayload(data: CachedWorkflowData): CachedWorkflowData {
  const workflows = Array.from(new Set(data.workflows)).slice(0, MAX_CACHED_WORKFLOWS);
  const seenJobKey = new Set<string>();
  const jobs: CachedWorkflowData["jobs"] = [];
  for (const job of data.jobs) {
    const key = `${job.workflow_name}::${job.job_name}`;
    if (seenJobKey.has(key)) continue;
    seenJobKey.add(key);
    jobs.push(job);
    if (jobs.length >= MAX_CACHED_JOBS) break;
  }
  return {
    ...data,
    workflows,
    jobs
  };
}

/**
 * Fetches workflow and job names from the API
 */
async function fetchWorkflowAndJobNames(api: ReturnType<typeof useApi>, org: NamedOrg): Promise<CachedWorkflowData> {
  try {
    // Fetch workflow filter options
    const workflowResponse = await api.get(`${orgApiPath(org)}/metrics/actions/workflows/runs/filter-options`);
    const workflows = workflowResponse.data.workflows || [];

    // Fetch job filter options
    const jobResponse = await api.get(`${orgApiPath(org)}/metrics/actions/jobs/runs/filter-options`);
    const jobs = jobResponse.data.jobs || [];
    return normalizeWorkflowCachePayload({
      workflows,
      jobs,
      timestamp: Date.now()
    });
  } catch (error) {
    console.error("Error fetching workflow/job names:", error);
    return {
      workflows: [],
      jobs: [],
      timestamp: Date.now()
    };
  }
}

/**
 * Gets cached data from localStorage
 */
function getCachedData(org: NamedOrg): CachedWorkflowData | null {
  const key = getWorkflowCacheKey(org);
  const cached = safeLocalStorage.get(key);
  if (!cached) return null;

  // If an old cache entry is huge, drop it to avoid quota pressure.
  if (getUtf8BytesLength(cached) > MAX_CACHE_BYTES_PER_ORG) {
    safeLocalStorage.remove(key);
    return null;
  }
  try {
    const parsed = JSON.parse(cached);
    const validated = CachedWorkflowData.parse(parsed);

    // Check if cache is still valid
    if (Date.now() - validated.timestamp > CACHE_DURATION) {
      safeLocalStorage.remove(key);
      return null;
    }
    return validated;
  } catch (error) {
    console.error("Error reading cache:", error);
    return null;
  }
}

/**
 * Sets cached data in localStorage
 */
function setCachedData(org: NamedOrg, data: CachedWorkflowData) {
  const normalized = normalizeWorkflowCachePayload(data);
  const key = getWorkflowCacheKey(org);
  const serialized = JSON.stringify(normalized);

  // Hard size cap: if this org's cache would exceed our budget, don't cache at all.
  // (Avoids "partial" or repeated eviction behavior; the cache is just a UX optimization.)
  if (getUtf8BytesLength(serialized) > MAX_CACHE_BYTES_PER_ORG) {
    return;
  }
  const result = safeLocalStorage.set(key, serialized, {
    maxBytes: MAX_CACHE_BYTES_PER_ORG,
    evict: {
      prefix: CACHE_KEY_PREFIX,
      exceptKey: key,
      strategy: "oldestByTimestamp"
    },
    getTimestamp: (rawValue: string) => {
      try {
        const parsed = JSON.parse(rawValue);
        const validated = CachedWorkflowData.safeParse(parsed);
        return validated.success ? validated.data.timestamp : 0;
      } catch {
        return 0;
      }
    }
  });
  if (!result.ok && result.reason === "quota") {
    // Best-effort: evict older caches manually and retry
    evictOldWorkflowCacheEntries(key);
    safeLocalStorage.set(key, serialized);
  }
}

/**
 * Hook to fetch and cache workflow/job names
 * Loads on app initialization and caches in browser storage
 */
export function useWorkflowCache(t0) {
  const $ = _c(20);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    enabled: t2
  } = t1;
  const enabled = t2 === undefined ? true : t2;
  const api = useApi();
  const {
    activeNamedOrg
  } = useActiveGithubOrgForUserNonSuspense();
  let t3;
  if ($[2] !== activeNamedOrg) {
    t3 = ["workflowCache", activeNamedOrg];
    $[2] = activeNamedOrg;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== activeNamedOrg || $[5] !== api) {
    t4 = async () => {
      if (!activeNamedOrg) {
        return null;
      }
      const cached = getCachedData(activeNamedOrg);
      if (cached) {
        return cached;
      }
      const freshData = await fetchWorkflowAndJobNames(api, activeNamedOrg);
      setCachedData(activeNamedOrg, freshData);
      return freshData;
    };
    $[4] = activeNamedOrg;
    $[5] = api;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const t5 = enabled && !!activeNamedOrg;
  let t6;
  if ($[7] !== t5) {
    t6 = {
      enabled: t5,
      staleTime: CACHE_DURATION,
      cacheTime: CACHE_DURATION,
      refetchOnWindowFocus: false,
      refetchOnMount: false
    };
    $[7] = t5;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const {
    data,
    isLoading
  } = useQuery(t3, t4, t6);
  let t7;
  if ($[9] !== data?.workflows) {
    t7 = data?.workflows || [];
    $[9] = data?.workflows;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== data?.jobs) {
    t8 = data?.jobs || [];
    $[11] = data?.jobs;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== activeNamedOrg) {
    t9 = () => {
      if (activeNamedOrg) {
        safeLocalStorage.remove(getWorkflowCacheKey(activeNamedOrg));
      }
    };
    $[13] = activeNamedOrg;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== isLoading || $[16] !== t7 || $[17] !== t8 || $[18] !== t9) {
    t10 = {
      workflows: t7,
      jobs: t8,
      isLoading,
      invalidateCache: t9
    };
    $[15] = isLoading;
    $[16] = t7;
    $[17] = t8;
    $[18] = t9;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  return t10;
}